// AS TRAINING — Food & Sports Nutrition Guide (evidence-based)

export const FOOD_INTRO = {
  title: "GUÍA DE ALIMENTACIÓN AS TRAINING",
  what_is:
    "Comer bien no es una dieta, es una estrategia. La nutrición deportiva combina calorías, macronutrientes, timing y hábitos para alcanzar tu objetivo físico sin renunciar al placer de comer.",
  principles: [
    { title: "Prioriza los alimentos reales", desc: "80% de tu dieta debe venir de alimentos mínimamente procesados: carnes, pescados, huevos, lácteos, frutas, verduras, cereales integrales, legumbres, frutos secos y aceites saludables." },
    { title: "Calorías primero, macros después", desc: "El balance calórico determina si ganas o pierdes peso. Los macronutrientes determinan la calidad del cambio (músculo vs grasa)." },
    { title: "Proteína en cada comida", desc: "Distribuye tu proteína en 3-5 tomas diarias de 20-40 g cada una para maximizar la síntesis muscular." },
    { title: "Hidratación estratégica", desc: "35 ml de agua por kg de peso al día. Suma 500 - 1000 ml por hora de entrenamiento." },
    { title: "Duerme para digerir", desc: "El descanso regula hormonas del apetito (leptina y grelina). Menos sueño = más hambre y peores decisiones." },
    { title: "Consistencia > Perfección", desc: "Mejor comer bien el 90% de la semana durante meses que ser perfecto una semana y abandonar." },
  ],
};

export const MACROS = [
  {
    id: "protein",
    name: "Proteína",
    icon: "muscle",
    color: "green",
    what: "Aminoácidos que reparan y construyen tejido muscular. El macronutriente más saciante.",
    kcal_per_gram: 4,
    daily_needs: {
      sedentary: "0.8 - 1.0 g/kg",
      active: "1.4 - 1.8 g/kg",
      athlete: "1.8 - 2.2 g/kg",
      cutting: "2.0 - 2.4 g/kg",
    },
    sources_high: ["Pechuga de pollo", "Pescado blanco", "Atún", "Claras de huevo", "Carne magra de res", "Queso cottage"],
    sources_medium: ["Huevo entero", "Yogur griego natural", "Queso mozzarella", "Tofu firme", "Tempeh", "Salmón"],
    sources_plant: ["Legumbres (lentejas, garbanzos, alubias)", "Tofu", "Tempeh", "Edamame", "Seitán", "Quinoa"],
    tips: [
      "Consume 20 - 40 g de proteína por comida.",
      "Distribuye en 3 - 5 tomas al día.",
      "Incluye una fuente completa en desayuno y post-entreno.",
    ],
  },
  {
    id: "carbs",
    name: "Carbohidratos",
    icon: "energy",
    color: "yellow",
    what: "La fuente principal de energía para entrenamientos intensos y funciones cerebrales.",
    kcal_per_gram: 4,
    daily_needs: {
      sedentary: "2 - 3 g/kg",
      active: "3 - 5 g/kg",
      athlete: "5 - 7 g/kg",
      cutting: "2 - 4 g/kg",
    },
    sources_slow: ["Avena integral", "Arroz integral", "Pan integral", "Batata / Boniato", "Quinoa", "Legumbres"],
    sources_fast: ["Arroz blanco", "Pasta", "Pan blanco", "Miel", "Frutas maduras", "Bebidas deportivas"],
    sources_fiber: ["Vegetales de hoja verde", "Brócoli", "Coliflor", "Berries", "Manzana", "Pera"],
    tips: [
      "Prioriza carbohidratos complejos (avena, arroz integral, batata).",
      "Los rápidos (arroz blanco, plátano) son ideales pre y post-entreno.",
      "Ajusta la cantidad según tu volumen de entrenamiento.",
    ],
  },
  {
    id: "fats",
    name: "Grasas",
    icon: "oil",
    color: "amber",
    what: "Producen hormonas, protegen órganos y aportan energía sostenida. Vitales para la salud.",
    kcal_per_gram: 9,
    daily_needs: {
      sedentary: "0.8 - 1.0 g/kg",
      active: "0.8 - 1.2 g/kg",
      athlete: "1.0 - 1.5 g/kg",
      cutting: "0.6 - 0.8 g/kg",
    },
    sources_mono: ["Aceite de oliva virgen extra", "Aguacate / Palta", "Almendras", "Cacahuetes / Maní", "Aceitunas"],
    sources_poly: ["Salmón", "Sardinas", "Nueces", "Semillas de chía", "Semillas de lino", "Aceite de linaza"],
    sources_saturated: ["Yema de huevo", "Coco / Aceite de coco", "Chocolate 85%+", "Carne roja magra", "Queso"],
    tips: [
      "Mantén al menos 0.6 g/kg diarios para no afectar hormonas.",
      "Prioriza mono y poliinsaturadas (oliva, pescados grasos, frutos secos).",
      "Modera las saturadas y evita las trans (fritos industriales).",
    ],
  },
];

export const CALORIC_STATES = [
  {
    id: "deficit",
    label: "Déficit calórico",
    subtitle: "Objetivo: pérdida de grasa",
    adjustment: "-10% a -20% del gasto",
    example: "Gasto 2500 kcal → comer 2000 - 2250 kcal",
    protein_hint: "Sube la proteína a 2.0 - 2.4 g/kg para preservar músculo",
    color: "green",
  },
  {
    id: "maintenance",
    label: "Mantenimiento",
    subtitle: "Objetivo: recomposición o descanso",
    adjustment: "±5% del gasto",
    example: "Gasto 2500 kcal → comer 2375 - 2625 kcal",
    protein_hint: "1.8 - 2.2 g/kg. Ideal para recomposición corporal",
    color: "white",
  },
  {
    id: "surplus",
    label: "Superávit calórico",
    subtitle: "Objetivo: ganancia muscular",
    adjustment: "+10% a +20% del gasto",
    example: "Gasto 2500 kcal → comer 2750 - 3000 kcal",
    protein_hint: "1.8 - 2.2 g/kg. Sube carbohidratos para energía",
    color: "green",
  },
];

export const MEAL_TEMPLATES = [
  {
    id: "breakfast",
    name: "Desayuno",
    when: "Al despertar / 1-2 h antes de entrenar",
    formula: "Proteína + Carbohidrato complejo + Grasa saludable + Fruta",
    examples: [
      "Avena + claras + plátano + almendras",
      "Tortilla de 3 huevos + pan integral + aguacate + tomate",
      "Yogur griego + granola + berries + nueces",
      "Panqueques de avena + proteína whey + miel + frutos rojos",
    ],
  },
  {
    id: "lunch",
    name: "Almuerzo",
    when: "Comida principal del día",
    formula: "Proteína magra + Carbohidrato + Vegetales + Grasa saludable",
    examples: [
      "Pechuga de pollo + arroz integral + brócoli + aceite de oliva",
      "Salmón al horno + batata + espinacas + aguacate",
      "Ternera magra + quinoa + vegetales asados + hummus",
      "Atún + pasta integral + espárragos + aceite de oliva",
    ],
  },
  {
    id: "snack",
    name: "Merienda / Snack",
    when: "Entre comidas / Pre-entreno",
    formula: "Proteína + Carbohidrato rápido o Fibra",
    examples: [
      "Batido de proteína + plátano",
      "Yogur griego + frutos rojos + granola",
      "Manzana + mantequilla de cacahuete natural",
      "Requesón + piña + canela",
    ],
  },
  {
    id: "post_workout",
    name: "Post-entreno",
    when: "En los 30-60 min después de entrenar",
    formula: "Proteína rápida + Carbohidrato de rápida absorción",
    examples: [
      "Batido de whey + plátano",
      "Claras + pan integral con miel",
      "Yogur griego + frutas + miel",
      "Pechuga de pollo + arroz blanco + zanahoria",
    ],
  },
  {
    id: "dinner",
    name: "Cena",
    when: "2-3 h antes de dormir",
    formula: "Proteína + Vegetales + Grasa saludable (poca cantidad de carbo)",
    examples: [
      "Pescado blanco + ensalada mixta + aceite de oliva",
      "Tortilla + vegetales salteados + aguacate",
      "Pechuga de pavo + calabacín asado + hummus",
      "Requesón + kiwi + almendras (opción ligera)",
    ],
  },
  {
    id: "bedtime",
    name: "Antes de dormir",
    when: "Opcional, ideal 30 min antes",
    formula: "Proteína lenta (caseína) + Grasa saludable",
    examples: [
      "Queso cottage + nueces",
      "Yogur griego natural + almendras",
      "Batido de caseína + mantequilla de almendras",
      "Requesón + canela",
    ],
  },
];

export const HYDRATION = {
  daily_base: "35 ml por kg de peso al día (ej. 70 kg → 2.4 L)",
  workout_add: "500 - 1000 ml por hora de entrenamiento",
  hot_climate: "Suma 500 - 1000 ml extra en clima cálido o si sudas mucho",
  signs_low: ["Orina oscura", "Boca seca", "Fatiga temprana", "Calambres", "Dolor de cabeza"],
  tips: [
    "Bebe al despertar: 300-500 ml para rehidratar tras dormir.",
    "Ten una botella a la vista durante todo el día.",
    "Bebe 500 ml en la comida previa al entreno.",
    "En sesiones >60 min o calor, añade electrolitos.",
  ],
};

export const SUBSTITUTIONS = [
  { from: "Arroz blanco", to: ["Arroz integral", "Quinoa", "Cuscús integral", "Batata"], group: "Carbohidratos" },
  { from: "Pan blanco", to: ["Pan integral", "Pan de centeno", "Tortitas de arroz", "Wraps integrales"], group: "Carbohidratos" },
  { from: "Pasta", to: ["Pasta integral", "Pasta de legumbres", "Fideos de calabacín", "Fideos de arroz"], group: "Carbohidratos" },
  { from: "Pechuga de pollo", to: ["Pechuga de pavo", "Pescado blanco", "Tofu firme", "Ternera magra"], group: "Proteína" },
  { from: "Huevo entero", to: ["Claras (2 = 1 huevo)", "Tofu revuelto", "Yogur griego"], group: "Proteína" },
  { from: "Yogur griego", to: ["Skyr", "Queso cottage", "Kéfir alto en proteína"], group: "Proteína" },
  { from: "Leche entera", to: ["Leche desnatada", "Bebida de almendra sin azúcar", "Bebida de soja alta en proteína"], group: "Lácteos" },
  { from: "Mantequilla", to: ["Aceite de oliva virgen extra", "Aguacate", "Ghee (moderación)"], group: "Grasas" },
  { from: "Azúcar", to: ["Miel cruda (moderación)", "Dátiles", "Stevia", "Eritritol"], group: "Endulzantes" },
  { from: "Refrescos", to: ["Agua con gas + limón", "Té helado sin azúcar", "Kombucha", "Agua mineral"], group: "Bebidas" },
];

export const PRACTICAL_TIPS = [
  {
    title: "Compra inteligente",
    items: [
      "Ve al supermercado con lista y sin hambre.",
      "80% del carro debe ser el perímetro del súper (frescos).",
      "Compra proteína, cereales y legumbres a granel.",
      "Congela porciones para no desperdiciar.",
    ],
  },
  {
    title: "Batch cooking (cocina por lotes)",
    items: [
      "Dedica 2 horas a la semana a cocinar de golpe.",
      "Cocina 1 kg de proteína, 500 g de arroz/quinoa y verduras al horno.",
      "Divide en tuppers para 4-5 comidas.",
      "Ahorra tiempo, dinero y evita tentaciones.",
    ],
  },
  {
    title: "Come fuera sin sabotear",
    items: [
      "Elige proteína magra a la plancha o al horno.",
      "Sustituye patatas fritas por ensalada o verdura.",
      "Pide salsas y aderezos aparte.",
      "Comparte los postres o toma solo 3-4 cucharadas.",
    ],
  },
  {
    title: "Errores comunes",
    items: [
      "Saltarse comidas y llegar con hambre a la siguiente.",
      "Beber calorías (jugos, sodas, alcohol).",
      "Cocinar sin planificación diaria (lleva a decisiones malas).",
      "Restringir en exceso — genera atracones.",
    ],
  },
];

export const FOOD_FAQ = [
  {
    q: "¿Necesito comer 6 veces al día para ganar músculo?",
    a: "No. Lo que importa es el total diario de calorías y proteína. Puedes lograrlo con 3, 4, 5 o 6 comidas. Elige la frecuencia que se adapte a tu rutina.",
  },
  {
    q: "¿Los carbohidratos por la noche engordan?",
    a: "No. Lo que importa es el total calórico del día. Puedes comer carbohidratos en la cena sin problema, sobre todo si entrenas por la tarde/noche.",
  },
  {
    q: "¿Cuántas comidas 'trampa' puedo tener?",
    a: "1 comida libre a la semana bien planeada no afecta tu progreso. Un día entero de excesos sí puede borrar el déficit semanal.",
  },
  {
    q: "¿Es mejor comer antes o después de entrenar?",
    a: "Ambos son importantes. Come 1-2 h antes (proteína + carbo lento) y dentro de la hora post-entreno (proteína + carbo rápido).",
  },
  {
    q: "¿Puedo entrenar en ayunas?",
    a: "Sí, si es entrenamiento suave-moderado. Para sesiones intensas o largas, come 1-2 h antes para rendir al máximo.",
  },
  {
    q: "¿Los edulcorantes engordan?",
    a: "No aportan calorías y no afectan el peso en cantidades normales. Son útiles para reducir azúcar añadido.",
  },
  {
    q: "¿Cuánta fruta puedo comer al día?",
    a: "2-4 porciones. Aporta fibra, vitaminas y agua. Su fructosa NO es problemática dentro de una dieta balanceada.",
  },
  {
    q: "¿Debo evitar el gluten o los lácteos?",
    a: "Solo si tienes intolerancia diagnosticada. Sin diagnóstico, restringir sin necesidad reduce variedad y disfrute.",
  },
];
